/**
 * ════════════════════════════════════════════════════════════════════════════
 * Frontend Logger
 * ════════════════════════════════════════════════════════════════════════════
 * Дата: 24.01.2026
 * Назначение: Централизованный сбор логов с фронтенда
 * 
 * Возможности:
 *   - Глобальный перехват JS ошибок
 *   - Перехват unhandled Promise rejections
 *   - Batching (накопление перед отправкой)
 *   - Offline queue (localStorage)
 *   - PII scrubbing
 *   - sendBeacon для надёжной отправки
 */

// ════════════════════════════════════════════════════════════════════════════
// Types
// ════════════════════════════════════════════════════════════════════════════

export interface LogEvent {
  timestamp: string;
  level: 'debug' | 'info' | 'warning' | 'error';
  event: string;
  data?: Record<string, unknown>;
  url: string;
  userAgent: string;
  sessionId: string;
  userId?: string;
  stack?: string;
  componentStack?: string;
}

interface LoggerConfig {
  endpoint: string;
  flushInterval: number;
  maxBatchSize: number;
  debug: boolean;
}

// ════════════════════════════════════════════════════════════════════════════
// Frontend Logger Class
// ════════════════════════════════════════════════════════════════════════════

class FrontendLogger {
  private queue: LogEvent[] = [];
  private config: LoggerConfig;
  private sessionId: string;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private initialized = false;

  constructor(config?: Partial<LoggerConfig>) {
    this.config = {
      endpoint: '/api/v1/frontend-logs',
      flushInterval: 5000, // 5 секунд
      maxBatchSize: 20,
      debug: import.meta.env.DEV,
      ...config,
    };
    this.sessionId = this.generateSessionId();
  }

  /**
   * Инициализация логгера - вызывать один раз при старте приложения
   */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.setupGlobalHandlers();
    this.startFlushTimer();
    this.loadPendingLogs();

    if (this.config.debug) {
      console.log('[Logger] Initialized', { sessionId: this.sessionId });
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Глобальные обработчики ошибок
  // ──────────────────────────────────────────────────────────────────────────

  private setupGlobalHandlers(): void {
    // 1. Необработанные JS ошибки
    window.onerror = (message, source, lineno, colno, error) => {
      this.error('js_error', {
        message: String(message),
        source,
        lineno,
        colno,
        stack: error?.stack,
      });
      return false; // Продолжить стандартную обработку
    };

    // 2. Необработанные Promise rejections
    window.onunhandledrejection = (event) => {
      this.error('unhandled_promise_rejection', {
        reason: String(event.reason),
        stack: event.reason?.stack,
      });
    };

    // 3. Ошибки загрузки ресурсов (capture phase)
    window.addEventListener('error', (event) => {
      const target = event.target as HTMLElement;
      
      if (target instanceof HTMLImageElement) {
        this.warning('resource_load_failed', {
          type: 'image',
          src: target.src,
        });
      } else if (target instanceof HTMLScriptElement) {
        this.error('resource_load_failed', {
          type: 'script',
          src: target.src,
        });
      } else if (target instanceof HTMLLinkElement) {
        this.warning('resource_load_failed', {
          type: 'stylesheet',
          href: target.href,
        });
      }
    }, true);

    // 4. Отправка при закрытии страницы
    window.addEventListener('beforeunload', () => this.flush());
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Публичные методы логирования
  // ──────────────────────────────────────────────────────────────────────────

  debug(event: string, data?: Record<string, unknown>): void {
    this.log('debug', event, data);
  }

  info(event: string, data?: Record<string, unknown>): void {
    this.log('info', event, data);
  }

  warning(event: string, data?: Record<string, unknown>): void {
    this.log('warning', event, data);
  }

  error(event: string, data?: Record<string, unknown>): void {
    this.log('error', event, data);
    this.flush(); // Ошибки отправляем сразу
  }

  /**
   * Логирование React ошибок (из Error Boundary)
   */
  logReactError(error: Error, componentStack: string): void {
    this.log('error', 'react_render_error', {
      error_name: error.name,
      error_message: error.message,
      stack: error.stack,
    });
    // componentStack добавляем отдельно для поддержки типов
    const lastEvent = this.queue[this.queue.length - 1];
    if (lastEvent) {
      lastEvent.componentStack = componentStack;
    }
    this.flush();
  }

  /**
   * Логирование сетевых запросов
   */
  logNetworkRequest(
    method: string,
    url: string,
    status: number,
    durationMs: number,
    error?: string
  ): void {
    const level = status >= 500 ? 'error' : status >= 400 ? 'warning' : 'info';
    this.log(level, 'network_request', {
      method,
      url: this.maskURL(url),
      status,
      duration_ms: durationMs,
      ...(error && { error }),
    });

    // Ошибки 5xx отправляем сразу
    if (status >= 500) {
      this.flush();
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Внутренняя реализация
  // ──────────────────────────────────────────────────────────────────────────

  private log(
    level: LogEvent['level'],
    event: string,
    data?: Record<string, unknown>
  ): void {
    const logEvent: LogEvent = {
      timestamp: new Date().toISOString(),
      level,
      event,
      data: this.scrubPII(data),
      url: window.location.href,
      userAgent: navigator.userAgent,
      sessionId: this.sessionId,
      userId: this.getUserId(),
    };

    this.queue.push(logEvent);

    // Console output в dev режиме
    if (this.config.debug) {
      const consoleMethod = level === 'error' ? 'error' : 
                           level === 'warning' ? 'warn' : 
                           level === 'debug' ? 'debug' : 'log';
      console[consoleMethod](`[${level.toUpperCase()}] ${event}`, data);
    }

    // Не накапливать слишком много
    if (this.queue.length >= this.config.maxBatchSize) {
      this.flush();
    }
  }

  async flush(): Promise<void> {
    if (this.queue.length === 0) return;

    const batch = this.queue.splice(0, this.config.maxBatchSize);

    try {
      // Используем navigator.sendBeacon для надёжности
      const success = navigator.sendBeacon(
        this.config.endpoint,
        JSON.stringify(batch)
      );

      if (!success) {
        // Fallback на fetch
        await fetch(this.config.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(batch),
          keepalive: true,
        });
      }

      if (this.config.debug) {
        console.log('[Logger] Flushed', batch.length, 'events');
      }
    } catch (e) {
      // Не удалось отправить - сохраняем в localStorage
      this.saveToLocalStorage(batch);
      
      if (this.config.debug) {
        console.warn('[Logger] Failed to flush, saved to localStorage', e);
      }
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // PII Scrubbing
  // ──────────────────────────────────────────────────────────────────────────

  private scrubPII(
    data?: Record<string, unknown>
  ): Record<string, unknown> | undefined {
    if (!data) return data;

    const piiFields = [
      'password', 'token', 'phone', 'email', 'card', 'ssn',
      'passport', 'inn', 'snils', 'secret', 'key', 'auth',
      'credit', 'cvv', 'pin', 'пароль', 'телефон', 'почта',
    ];

    const scrubbed: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(data)) {
      const keyLower = key.toLowerCase();
      const isPII = piiFields.some(pii => keyLower.includes(pii));

      if (isPII) {
        scrubbed[key] = '[REDACTED]';
      } else if (value && typeof value === 'object' && !Array.isArray(value)) {
        scrubbed[key] = this.scrubPII(value as Record<string, unknown>);
      } else {
        scrubbed[key] = value;
      }
    }

    return scrubbed;
  }

  private maskURL(url: string): string {
    const sensitiveParams = ['token', 'key', 'secret', 'auth', 'password'];
    let masked = url;

    for (const param of sensitiveParams) {
      const regex = new RegExp(`([?&])${param}=[^&]*`, 'gi');
      masked = masked.replace(regex, `$1${param}=[REDACTED]`);
    }

    return masked;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // LocalStorage Queue
  // ──────────────────────────────────────────────────────────────────────────

  private saveToLocalStorage(batch: LogEvent[]): void {
    try {
      const key = 'medintegra_pending_logs';
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      const combined = [...existing, ...batch].slice(-100); // Максимум 100
      localStorage.setItem(key, JSON.stringify(combined));
    } catch {
      // localStorage недоступен или полон
    }
  }

  private loadPendingLogs(): void {
    try {
      const key = 'medintegra_pending_logs';
      const pending = JSON.parse(localStorage.getItem(key) || '[]');
      if (pending.length > 0) {
        this.queue.push(...pending);
        localStorage.removeItem(key);
        
        if (this.config.debug) {
          console.log('[Logger] Loaded', pending.length, 'pending logs');
        }
        
        // Сразу отправляем накопленные
        this.flush();
      }
    } catch {
      // Ignore
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Helpers
  // ──────────────────────────────────────────────────────────────────────────

  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => this.flush(), this.config.flushInterval);
  }

  private generateSessionId(): string {
    return 'sess_' + Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  }

  private getUserId(): string | undefined {
    // Пытаемся получить из auth store
    try {
      const authState = localStorage.getItem('auth-storage');
      if (authState) {
        const parsed = JSON.parse(authState);
        return parsed?.state?.user?.id;
      }
    } catch {
      // Ignore
    }
    return undefined;
  }

  /**
   * Получить текущий session ID
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Уничтожить логгер
   */
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush();
  }
}

// ════════════════════════════════════════════════════════════════════════════
// Export Singleton Instance
// ════════════════════════════════════════════════════════════════════════════

export const logger = new FrontendLogger();

// Для доступа из консоли браузера в dev режиме
if (import.meta.env.DEV) {
  (window as unknown as { __logger: FrontendLogger }).__logger = logger;
}

export default logger;
